// src/scripts/sqlApiHelper/edgeApi.js
import { auth, authReady } from "@/firebase";

const SUPABASE_URL = (process.env.VUE_APP_SUPABASE_URL || "").replace(/\/+$/, "");
const SUPABASE_ANON_KEY = process.env.VUE_APP_SUPABASE_ANON_KEY || "";

// Always derive from SUPABASE_URL to avoid accidental misrouting.
// (If you truly need an override later, add it back intentionally.)
const EDGE_BASE = SUPABASE_URL ? `${SUPABASE_URL}/functions/v1` : "";

/**
 * If your frontend still calls older slugs, map them here to the deployed ones.
 * This fixes cases like calling "stock-week-lines-upsert" when the deployed function is "stock-week-upsert".
 */
const FUNCTION_ALIASES = {
  "stock-week-lines-upsert": "stock-week-upsert",
  // Add more legacy -> current slugs here if needed.
};

// Any endpoint that relies on custom claims should force-refresh the token
const FORCE_REFRESH_FUNCTIONS = new Set([
  "admin-venues-upsert",
  "venues-upsert",
  "admin-venues-list",
  "venues-list",
  "sync-user-venue-from-firestore",
  // Add others only if you truly depend on newly-updated claims immediately.
]);

function safeJsonParse(text) {
  try {
    return JSON.parse(text);
  } catch {
    return null;
  }
}

// async function getFirebaseToken(forceRefresh = false) {
//   await authReady;
//   const user = auth.currentUser;
//   if (!user) throw new Error("Not signed in (no Firebase user)");
//   return await user.getIdToken(forceRefresh);
// }

async function getFirebaseToken(forceRefresh = false) {
  await authReady;
  const user = auth.currentUser;
  if (!user) throw new Error("Not signed in (no Firebase user)");

  // Force refresh if requested
  const token = await user.getIdToken(forceRefresh);

  // TEMP DEBUG: only log on forced refresh (admin endpoints)
  if (forceRefresh) {
    const r = await user.getIdTokenResult(true);
    // console.log("[EDGE][claims]", {
    //   uid: user.uid,
    //   email: user.email,
    //   admin: r.claims?.admin === true,
    //   operations: r.claims?.operations === true,
    //   // dump keys to spot mismatched names like is_admin / isAdmin / role etc.
    //   claimKeys: Object.keys(r.claims || {}),
    // });
  }

  return token;
}


/**
 * Call a Supabase Edge function with:
 * - Supabase anon headers (apikey + Authorization)
 * - Firebase ID token header (x-firebase-token)
 */
export async function callEdge(fnName, payload = {}, opts = {}) {
  if (!SUPABASE_ANON_KEY) throw new Error("Missing VUE_APP_SUPABASE_ANON_KEY");
  if (!SUPABASE_URL) throw new Error("Missing VUE_APP_SUPABASE_URL");
  if (!EDGE_BASE) throw new Error("Missing EDGE_BASE (derived from VUE_APP_SUPABASE_URL)");

  const resolvedFn = FUNCTION_ALIASES[fnName] || fnName;
  const url = `${EDGE_BASE}/${resolvedFn}`;

  const forceRefresh =
    typeof opts.forceRefresh === "boolean"
      ? opts.forceRefresh
      : FORCE_REFRESH_FUNCTIONS.has(resolvedFn);

  const firebaseToken = await getFirebaseToken(forceRefresh);

  const headers = {
    "content-type": "application/json",
    accept: "application/json",

    apikey: SUPABASE_ANON_KEY,
    authorization: `Bearer ${SUPABASE_ANON_KEY}`,

    "x-firebase-token": firebaseToken,
  };

  // // Helpful diagnostics (leave on while stabilising; remove later if you want)
  // console.log("[EDGE] fn", fnName, "=>", resolvedFn);
  // console.log("[EDGE] url", url);
  // console.log("[EDGE] headers", Object.keys(headers));

  try {
    const res = await fetch(url, {
      method: "POST",
      headers,
      body: JSON.stringify(payload ?? {}),
    });

    const text = await res.text();
    const data = safeJsonParse(text);

    if (!res.ok) {
      const msg =
        (data && (data.error || data.message)) ||
        text ||
        `HTTP ${res.status} calling ${resolvedFn}`;

      const err = new Error(msg);
      err.status = res.status;
      err.fnName = resolvedFn;
      err.response = data ?? text;
      throw err;
    }

    return data ?? text;
  } catch (e) {
    // Network / preflight / blocked requests land here as "Failed to fetch".
    // Re-throw with clear function + URL context.
    throw new Error(
      `Edge call failed (${resolvedFn}) at ${url}: ${e?.message || String(e)}`
    );
  }
}

export async function refreshFirebaseClaims() {
  await getFirebaseToken(true);
  return true;
}

// src/scripts/sqlApiHelper/edgeApi.js
// ...keep everything you already have above...

export async function getEdgeClaims(forceRefresh = false) {
  await authReady;
  const user = auth.currentUser;
  if (!user) return null;

  const tokenResult = await user.getIdTokenResult(!!forceRefresh);
  return tokenResult?.claims || null;
}
